import { Injectable } from '@angular/core'
import { HttpClient, HttpEvent, HttpEventType, HttpHeaders } from '@angular/common/http'
import { Observable, of, Subject } from 'rxjs'
import { map } from 'rxjs/operators'
import { NzUploadFile } from 'ng-zorro-antd/upload'
import { AnalysisRcaFilterModel } from '../../filters/analysis-rca-filter.modal'
import { HelperService } from '../helper.service'
import { PaginationModel } from '../../models/pagination.model'
import { environment } from '../../../environments/environment'
import IHistoryTrackingService from '../../pages/spas/common/history-tracking/i-history-tracking.service'
import { IChangeLogModel, IRequestLogModel } from '../../models/history-records-change.model'
import IAnalysisService from '../../pages/spas/common/analysis-components/i-analysis.service'
import { IAnalysisFilesContainerService } from '../../pages/spas/common/analysis-components/i-analysis-files-container.service'
import { IAnalysisHistoryService } from '../../pages/spas/common/analysis-components/i-analysis-history.service'
import { IDataCardNotification } from '../../models/data-card.model'

@Injectable({
  providedIn: 'root',
})
export class RootCauseAnalysisService
  implements
    IHistoryTrackingService,
    IAnalysisService,
    IAnalysisFilesContainerService,
    IAnalysisHistoryService {
  private static _allTags: { key: string; items: string[] }
  private static _allRCANo: { key: string; items: string[] }
  private static _images: any = {}

  private apiURL: string = environment.apiUrl + '/fault-statistics/rca'
  private _photosLoadSubject: Subject<{ id: number; name: string; mime: string }[]> = new Subject()
  private _photoDeleteSubject: Subject<{ id: number; name: string; mime: string }> = new Subject()
  private _rcaNoIsActiveSubject: Subject<boolean> = new Subject()
  private _notificationAssignmentSubject: Subject<IDataCardNotification[]> = new Subject()

  public allowedImgMimeTypes: string[] = ['image/png', 'image/jpeg', 'image/gif']
  public allowedAttachMimeTypes: string[] = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'application/json',
    'application/pdf',
    'application/xml',
    'text/plain',
  ]

  public get photosLoadSubject(): Subject<{ id: number; name: string; mime: string }[]> {
    return this._photosLoadSubject
  }

  public loadPhotos(photos: { id: number; name: string; mime: string }[]): void {
    this._photosLoadSubject.next(photos)
  }

  public get photoDeleteSubject(): Subject<{ id: number; name: string; mime: string }> {
    return this._photoDeleteSubject
  }

  public deletePhoto(photo: { id: number; name: string; mime: string }): void {
    this._photoDeleteSubject.next(photo)
  }

  public get rcaNoIsActiveSubject(): Subject<boolean> {
    return this._rcaNoIsActiveSubject
  }

  public rcaNoChangeActiveStatus(isActive: boolean): void {
    this._rcaNoIsActiveSubject.next(isActive)
  }

  public get notificationAssignmentSubject(): Subject<IDataCardNotification[]> {
    return this._notificationAssignmentSubject
  }

  public notificationAssignment(notifications: IDataCardNotification[]): void {
    this._notificationAssignmentSubject.next(notifications)
  }

  constructor(private http: HttpClient) {}

  private getHeaders(): HttpHeaders {
    return new HttpHeaders().set('Content-Type', 'application/json')
  }

  public getRCAReportData(
    pagination: PaginationModel,
    filter: AnalysisRcaFilterModel,
    active: boolean,
  ): Observable<any> {
    const newFilter: any = filter ? filter : { active: active ? 1 : 0 }
    newFilter.active = active ? 1 : 0
    const query: string = HelperService.complexObjectToQuery({
      pagination: pagination,
      filter: newFilter,
    })
    const url: string = `${this.apiURL}/rca-report-data?` + query
    return this.http
      .get<any>(url, { headers: this.getHeaders() })
      .pipe(
        map(resp => {
          return resp
        }),
      )
  }

  public getTagItems(key: string = '', size: number = 10): Observable<string[]> {
    if (RootCauseAnalysisService._allTags && RootCauseAnalysisService._allTags.key === key) {
      return of(RootCauseAnalysisService._allTags.items)
    }
    let url: string = this.apiURL + '/tag-items'
    if (key.length > 0) {
      url += '?key=' + key
    }
    return this.http.get(url, { headers: this.getHeaders() }).pipe(
      map((resource: string[]) => {
        RootCauseAnalysisService._allTags = {
          key: key,
          items: resource,
        }
        return resource
      }),
    )
  }

  public getRCAItems(key: string = '', size: number = 10): Observable<string[]> {
    if (RootCauseAnalysisService._allRCANo && RootCauseAnalysisService._allRCANo.key === key) {
      return of(RootCauseAnalysisService._allRCANo.items)
    }
    let url: string = this.apiURL + '/rca-items'
    if (key.length > 0) {
      url += '?key=' + key
    }
    return this.http.get(url, { headers: this.getHeaders() }).pipe(
      map((resource: string[]) => {
        RootCauseAnalysisService._allRCANo = {
          key: key,
          items: resource,
        }
        return resource
      }),
    )
  }

  public getEligibleTags(): Observable<string[]> {
    const url: string = this.apiURL + '/eligible-tags'
    return this.http.get(url, { headers: this.getHeaders() }).pipe(
      map((resource: string[]) => {
        return resource
      }),
    )
  }

  public getTagInfo(tag: string): Observable<any> {
    const url: string = this.apiURL + '/tag-info?tag=' + encodeURIComponent(tag)
    return this.http.get(url, { headers: this.getHeaders() }).pipe(
      map((resource: any) => {
        return resource
      }),
    )
  }

  public getTravelTimeMonitoringURLParams(tag: string): any {
    const timeFrame: Date[] = HelperService.getYearlyTimeFrame(1)
    return {
      filter: JSON.stringify(
        HelperService.getObjectWithoutNullValues({
          tagName: tag,
          startTime: timeFrame[0],
          endTime: timeFrame[1],
        }),
      ),
    }
  }

  public getFinalElementActivationsURLParams(
    type: 'op' | 'cf',
    tag: string,
    statusMainElem: string[],
  ): any {
    const timeFrame: Date[] = HelperService.getYearlyTimeFrame(1)
    const mainElem: string[] = []
    switch (true) {
      case type === 'cf':
        const cf: string[] = statusMainElem.filter(
          o => (o && o.toLowerCase() === 'slow ss') || (o && o.toLowerCase() === 'no response ss'),
        )
        mainElem.push(...cf)
        break
    }
    return {
      filter: JSON.stringify(
        HelperService.getObjectWithoutNullValues({
          tagName: tag,
          startTime: timeFrame[0],
          endTime: timeFrame[1],
          mainElem: mainElem,
        }),
      ),
    }
  }

  public registerTag(
    data: any,
    imageFiles: NzUploadFile[] = [],
    attachmentFiles: NzUploadFile[] = [],
  ): Observable<any> {
    const formData: FormData = new FormData()
    formData.append('data', JSON.stringify(data))

    imageFiles.forEach((file: any) => {
      formData.append('images', file)
    })

    attachmentFiles.forEach((file: any) => {
      formData.append('attachments', file)
    })

    const url: string = this.apiURL + '/tag/register'
    return this.http.post(url, formData).pipe(
      map(resource => {
        return resource
      }),
    )
  }

  public updateTag(
    tag: string,
    rcaFaultNo: string,
    data: any,
    imageFiles: NzUploadFile[] = [],
    attachmentFiles: NzUploadFile[] = [],
    closeTag: boolean = false,
  ): Observable<any> {
    const formData: FormData = new FormData()
    formData.append('data', JSON.stringify(data))

    imageFiles.forEach((file: any) => {
      formData.append('images', file)
    })

    attachmentFiles.forEach((file: any) => {
      formData.append('attachments', file)
    })

    const url: string =
      this.apiURL +
      '/tag/update/' +
      encodeURIComponent(rcaFaultNo) +
      (closeTag ? '?closeTag=1&tag=' : '?tag=') +
      encodeURIComponent(tag)
    return this.http.post(url, formData).pipe(
      map(resource => {
        return resource
      }),
    )
  }

  public openTag(tag: string, rcaNo: string): Observable<any> {
    const url: string =
      this.apiURL + '/tag/open/' + encodeURIComponent(rcaNo) + '?tag=' + encodeURIComponent(tag)
    return this.http.put(url, { headers: this.getHeaders() }).pipe(
      map((resource: any) => {
        return resource
      }),
    )
  }

  public getTagMainForm(tag: string, rcaFaultNo: string): Observable<any> {
    const url: string =
      this.apiURL +
      '/tag/main-form/' +
      encodeURIComponent(rcaFaultNo) +
      '?tag=' +
      encodeURIComponent(tag)
    return this.http.get(url, { headers: this.getHeaders() }).pipe(
      map((resource: any) => {
        return resource
      }),
    )
  }

  public deleteFile(id: number, tag: string, rcaFaultNo: string): Observable<any> {
    const url: string =
      this.apiURL +
      '/file/delete/' +
      encodeURIComponent(rcaFaultNo) +
      '/' +
      encodeURIComponent(id) +
      '?tag=' +
      encodeURIComponent(tag)
    return this.http.delete(url, { headers: this.getHeaders() }).pipe(
      map((resource: string) => {
        return resource
      }),
    )
  }

  public notifyFileDeletion(file: { id: number; name: string; mime: string }): void {
    this.deletePhoto(file)
  }

  public downloadFile(id: number, tag: string, rcaFaultNo: string): Observable<any> {
    const url: string =
      this.apiURL +
      '/file/download/' +
      encodeURIComponent(rcaFaultNo) +
      '/' +
      encodeURIComponent(id) +
      '?tag=' +
      encodeURIComponent(tag)
    return this.http
      .get(url, { reportProgress: true, observe: 'events' })
      .pipe(map(event => this.handleEventMessage(event)))
  }

  private handleEventMessage(event: HttpEvent<any>) {
    switch (event.type) {
      case HttpEventType.DownloadProgress:
      case HttpEventType.UploadProgress:
        return this.fileUploadProgress(event)
      case HttpEventType.Response:
        return this.apiResponse(event)
      default:
        return `Unknown event type`
    }
  }

  private fileUploadProgress(event: any): { status: string; message: number } {
    const percent: number = Math.round((100 * event.loaded) / event.total)
    return { status: 'Progress', message: percent }
  }

  private apiResponse(event: any) {
    return event.body
  }

  getHistoryEvents(
    pagination: PaginationModel,
    params?: any,
  ): Observable<{ count: number; items: IRequestLogModel[] }> {
    const query: string = HelperService.complexObjectToQuery({
      pagination: pagination,
      tag: params.tag,
    })
    const url: string =
      this.apiURL + '/history/events/' + encodeURIComponent(params.rcaFaultNo) + '?' + query
    return this.http.get(url, { headers: this.getHeaders() }).pipe(
      map((resource: { count: number; items: IRequestLogModel[] }) => {
        return resource
      }),
    )
  }

  getHistoryEventItems(
    processId: string,
    pagination: PaginationModel,
  ): Observable<{ count: number; items: IChangeLogModel[] }> {
    const query: string = HelperService.complexObjectToQuery({ pagination: pagination })
    const url: string =
      this.apiURL + '/history/events/' + encodeURIComponent(processId) + '/items?' + query
    return this.http.get(url, { headers: this.getHeaders() }).pipe(
      map((resource: { count: number; items: IChangeLogModel[] }) => {
        return resource
      }),
    )
  }

  /** Photos */

  public getTagPrevPhotos(tag: string, rcaFaultNo: string): Observable<any[]> {
    const url: string =
      this.apiURL +
      '/tag/' +
      encodeURIComponent(rcaFaultNo) +
      '/photos/prev?tag=' +
      encodeURIComponent(tag)
    return this.http.get(url).pipe(
      map((response: any[]) => {
        return response
      }),
    )
  }

  public getTagPhotoBase64(
    id: number,
    tag: string,
    rcaFaultNo: string,
    thumb: boolean = false,
  ): Observable<any> {
    // Get from cache IF exists
    if (!thumb && RootCauseAnalysisService._images[id]) {
      return of(RootCauseAnalysisService._images[id])
    }

    const url: string =
      this.apiURL +
      '/file/download/' +
      encodeURIComponent(rcaFaultNo) +
      '/' +
      encodeURIComponent(id) +
      (thumb ? '?thumb=1&tag=' : '?tag=') +
      encodeURIComponent(tag)
    return this.http.get(url).pipe(
      map((response: any) => {
        // Cache it only IF not thumbnail
        if (!thumb && !RootCauseAnalysisService._images[id]) {
          RootCauseAnalysisService._images[id] = {
            base64: response.base64,
            mime: response.mime,
          }
        }
        return {
          base64: response.base64,
          mime: response.mime,
        }
      }),
    )
  }

  /** Notifications **/

  public getNotifications(tag: string, rcaFaultNo: string): Observable<any> {
    const url: string =
      this.apiURL +
      '/tag/' +
      encodeURIComponent(rcaFaultNo) +
      '/notifications?tag=' +
      encodeURIComponent(tag)
    return this.http.get(url).pipe(
      map((response: any) => {
        return response
      }),
    )
  }

  public assignToRCA(
    tagName: string,
    notification: string,
    rcaNo: string,
    assign: boolean,
  ): Observable<any> {
    const data: any = {
      notification: notification,
      rcaNo: rcaNo,
      tagName: tagName,
      assigned: assign,
    }
    const url: string =
      this.apiURL + '/tag/' + encodeURIComponent(rcaNo) + '/notifications/assign-to-rca'
    return this.http.post(url, data).pipe(
      map((response: any) => {
        return response
      }),
    )
  }

  /** Work Orders **/

  public getWorkOrders(active: boolean): Observable<any> {
    const url: string = this.apiURL + `/work-orders?active=${active ? 1 : 0}`
    return this.http.get(url).pipe(
      map((response: any) => {
        return response
      }),
    )
  }

  public updateWorkOrderURL(workOrder: string, URL: string): Observable<any> {
    const url: string = this.apiURL + '/work-orders/' + encodeURIComponent(workOrder) + '/url'
    return this.http.put(url, { url: URL }, { headers: this.getHeaders() }).pipe(
      map((resource: string) => {
        return resource
      }),
    )
  }
}
